import { QUIZ_CATEGORIES } from "~/constants/questionnaire";
import { STORAGE_KEYS } from "~/types/Storage";
import { getStorageItem } from "~/lib/storage/Storage";

const ENDOMETRIOSIS_PAGE_NAMES = {
    PAGE_14: "the_minimum_age_to_begin_oestra_is_21",
    PAGE_15: "oestra_is_most_effective_when_started_before_65",
}

// Quiz styling configuration
type ButtonConfig = {
    className: string;
    hasIcon?: boolean;
};

type QuizStyleConfig = {
    backButton: ButtonConfig;
    nextButton: ButtonConfig;
    completeButton: ButtonConfig;
    firstButton?: ButtonConfig;
};

const quizStyles = {
    backButton: {
        className: "rounded-full w-[56px] shrink-0 h-[56px] !p-0 !min-w-[56px] flex items-center justify-center !border-none",
        hasIcon: true,
    },
    nextButton: {
        className: "w-full ml-[16px]",
    },
    completeButton: {
        className: "w-full ml-[16px]",
    },    
}

const miniQuizStyles = {
    backButton: {
        className: "rounded-full w-[56px] shrink-0 h-[56px] !p-0 !min-w-[56px] flex items-center justify-center !border-none",
        hasIcon: true,
    },
    nextButton: {
        className: "w-full ml-[16px] !bg-heroBlue",
    },
    completeButton: {
        className: "w-full ml-[16px] !bg-heroBlue",
    },
    firstButton: {
        className: "!bg-heroBlue",
    },
}

export const QUIZ_STYLES_CONFIG: Record<string, QuizStyleConfig> = {
    [QUIZ_CATEGORIES.OESTRA_ENDO_PRE_PURCHASE]: quizStyles,
    [QUIZ_CATEGORIES.FACE_CREAM]: quizStyles,
    [QUIZ_CATEGORIES.OESTRA_CREAM_UPSELL_SHORT]: miniQuizStyles,
    [QUIZ_CATEGORIES.FACE_CREAM_UPSELL_SHORT]: miniQuizStyles,
    // Example for other categories
    // [QUIZ_CATEGORIES.FACE_CREAM]: {
    //     backButton: { className: "...", hasIcon: false },
    //     nextButton: { className: "..." },
    //     completeButton: { className: "..." },
    // },
};

export const getQuizButtonStyles = (category: string, buttonType: keyof QuizStyleConfig): ButtonConfig => {
    const config = QUIZ_STYLES_CONFIG[category];
    return config?.[buttonType] || { className: "", hasIcon: false };
};

export const isViewEndometriosisNavigation = (category: string, pageName: string) => {    
    if (category !== QUIZ_CATEGORIES.OESTRA_ENDO_PRE_PURCHASE) return true;

    return pageName !== ENDOMETRIOSIS_PAGE_NAMES.PAGE_14 && pageName !== ENDOMETRIOSIS_PAGE_NAMES.PAGE_15;
}

// Configuration for categories that should use NextButton instead of StartButton on first page (pageNo === 0)
const USE_NEXT_BUTTON_ON_FIRST_PAGE: string[] = [
    QUIZ_CATEGORIES.OESTRA_CREAM_UPSELL_SHORT,
    QUIZ_CATEGORIES.FACE_CREAM_UPSELL_SHORT,
    // Add other categories here if needed
];

export const shouldUseNextButtonOnFirstPage = (category: string): boolean => {
    return USE_NEXT_BUTTON_ON_FIRST_PAGE.includes(category);
};

type QuizUserInfo = Partial<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
}>;

const USER_INFO_FIELDS = [
    "first_name",
    "last_name",
    "email",
    "phone",
] as const;

const getNonEmptyString = (value: unknown): string | undefined => {
    if (typeof value !== "string" || value.trim().length === 0) {
        return undefined;
    }

    return value.trim();
};

const sanitizeQuizUserInfo = (value: unknown): QuizUserInfo => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return {};
    }

    const quizData = value as Record<string, unknown>;
    const userInfo: QuizUserInfo = {};
    const firstName = getNonEmptyString(quizData.first_name)
        ?? getNonEmptyString(quizData.firstName);
    const lastName = getNonEmptyString(quizData.last_name)
        ?? getNonEmptyString(quizData.lastName);
    const email = getNonEmptyString(quizData.email);
    const phone = getNonEmptyString(quizData.phone);

    if (firstName) userInfo.first_name = firstName;
    if (lastName) userInfo.last_name = lastName;
    if (email) userInfo.email = email;
    if (phone) userInfo.phone = phone;

    return userInfo;
};

/**
 * True only for an object that carries at least one contact field, all of them
 * known and non-empty. Also the guard for the result of
 * `extractUserInfoFromFirstCompletedQuiz`: that returns `{}` rather than `null`
 * when there is nothing to prefill, so a plain truthiness check always passes.
 */
export const isQuizUserInfo = (value: unknown): value is QuizUserInfo => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }

    const entries = Object.entries(value);

    return entries.length > 0 && entries.every(([field, fieldValue]) => (
        USER_INFO_FIELDS.some((userInfoField) => userInfoField === field)
        && getNonEmptyString(fieldValue) !== undefined
    ));
};

// Extract information about the user from the first completed quiz
export const extractUserInfoFromFirstCompletedQuiz = (): QuizUserInfo => {
    // Create array of possible quiz data keys: category-specific keys + default key
    const possibleKeys = [
        ...Object.values(QUIZ_CATEGORIES).map(category => category + STORAGE_KEYS.QUIZ_DATA),
        STORAGE_KEYS.QUIZ_DATA
    ];

    for (const key of possibleKeys) {
        const quizDataString = getStorageItem(key);

        if (!quizDataString) {
            continue;
        }

        try {
            const quizData: unknown = JSON.parse(quizDataString);
            const userInfo = sanitizeQuizUserInfo(quizData);

            if (isQuizUserInfo(userInfo)) {
                return userInfo;
            }
        } catch {
            continue;
        }
    }

    return {};
};